import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {Subscription, timer} from 'rxjs';
import {ConstantsService} from '../fidelidade/ConstantsService';
import {MonitoradorPedidos} from '../fidelidade/MonitoradorPedidos';
import {FidelidadeComponent} from '../fidelidade/fidelidade.component';
import {AutorizacaoService} from '../services/autorizacao.service';
import {RotaGuardClient} from '../guards/auth-loja.guard';
import {EnumFiltroDePedidos, PedidosService} from '../services/pedidos.service';
import {VendasPedidoService} from '../services/vendas-pedido.service';
import {CaixaService} from '../pdv/services/caixa.service';
import {MesaService} from '../services/mesa.service';
import {EmpresasService} from '../superadmin/services/empresas.service';
import {ServidorImpressaoExecutorService} from '../services/servidor-impressao-executor.service';
import {FuncaoMenuV2} from '../componentes/menu-v2/menu-v2.catalogo';
import {criarCatalogoParaUsuario} from '../componentes/menu-v2/menu-v2.contexto';
import {UsoMenuV2} from '../componentes/menu-v2/menu-v2.uso';
import {apliqueFaseDoOnboarding} from '../componentes/menu-v2/menu-v2.fases';
import {EnumStatusPedido} from '../../../server/lib/emun/EnumStatusPedido';
import {EnumMomentoImprimirAuto} from '../../../server/lib/emun/EnumMomentoImprimirAuto';
import {TelaImprimirPedido} from '../fidelidade/TelaImprimirPedido';
import {ImpressaoService} from '../services/impressao.service';
import {NfceService} from '../services/nfce.service';
import {ContatosService} from '../services/contatos.service';
import {EntregadorService} from '../services/entregador.service';
import {
  abaDaEtapa, AbaComanda, CODIGO_DO_STATUS, EtapaComanda, ordeneParaOTrilho, PassoComanda, perguntaPagamento,
  proximoPasso, statusPermitidos
} from './home-celular.comandas';

/** O momento do dia decide o que a tela mostra primeiro; a permissão decide se cada bloco existe. */
export type MomentoHomeCelular = 'contaNova' | 'antes' | 'aberta' | 'fechou';

export type PeriodoVendasCelular = 'hoje' | 'ontem' | '7dias' | '30dias';

/** Uma comanda da lista: um pedido em aberto com o próximo passo, ou uma pendência agregada. */
export interface ComandaHomeCelular {
  id: string;
  etapa: EtapaComanda | null;
  aba: AbaComanda;
  /** Cor da comanda: a etapa, "atraso" por cima dela, ou "pendencia". */
  tom: string;
  rotulo: string;
  icone: string;
  titulo: string;
  meta: string;
  nota: string;
  valor: number | null;
  tempo: string;
  quente: boolean;
  itens: Array<{qtde: string, descricao: string, observacao: string}>;
  maisItens: number;
  /** null = sem passo permitido: o botão principal abre o pedido. */
  passo: PassoComanda | null;
  rota: string;
  pedido?: any;
}

/** Pedido em aberto há mais que isso não é "agora": é esquecido, e sai da lista para um aviso à parte. */
const MINUTOS_PEDIDO_ANTIGO = 12 * 60;
const LISTA_INICIAL = 3;
const LISTA_PASSO = 5;

/** Cor de reconhecimento por área do menu: pastel no fundo, ícone na cor forte. */
const COR_DO_GRUPO: {[grupo: string]: string} = {
  operacao: 'azul', cardapio: 'ceu', clientes: 'rosa', atendimento: 'roxo',
  gestao: 'verde', pay: 'teal', configuracoes: 'cinza', conta: 'cinza'
};

/** As portas de venda, na ordem em que aparecem; cada uma só existe se o catálogo a liberar. */
const PORTAS_DE_VENDA = [
  {id: 'pedidos/novo-v3', nome: 'Pedido', detalhe: 'entrega ou retirada', icone: 'fe-truck', cor: 'azul'},
  {id: 'pedidos/pdv/novo', nome: 'Balcão', detalhe: 'venda rápida', icone: 'fe-shopping-bag', cor: 'teal'},
  {id: 'pedidos-mesas', nome: 'Mesa', detalhe: 'abre a comanda', icone: 'fe-grid', cor: 'roxo'}
];

const DIAS_DA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** Origem do pedido → nome que o dono reconhece (o mesmo mapa da home do desktop). */
const NOME_DA_ORIGEM: {[chave: string]: string} = {
  loja: 'Cardápio (link)', admin: 'Painel', bot: 'WhatsApp / Mia', garcom: 'Garçom', balcao: 'Balcão / PDV',
  ifood: 'iFood', keeta: 'Keeta', '99food': '99Food', opendelivery: 'OpenDelivery', totem: 'Totem', api: 'API',
  multiloja: 'Multiloja', CENTRAL_PEDIDOS: 'Central de pedidos', REPOSICAO_INTERNA: 'Reposição interna'
};

const CORES_DA_ORIGEM = ['#3b46b8', '#6a3fd1', '#e8502e', '#0b7570', '#c0264f', '#a85a00', '#0a6fa8', '#4f5865'];

/**
 * Início pensado para o dono que só tem o celular. Não reaproveita os blocos da
 * home v2: a tela muda com o momento do dia (conta nova, antes de abrir, aberta,
 * depois de fechar) e abre pelo que está esperando o dono agora.
 *
 * Permissões: cada bloco checa a sua — vendas só com a operação de relatórios,
 * caixa com a de caixa, pausar com a de gerenciar loja, mesas e pedidos pelo
 * módulo. Vender, atalhos e "Todas as funções" saem do mesmo catálogo do
 * menu-v2, que já filtra por permissão, módulo e recurso.
 */
@Component({
  selector: 'app-home-celular',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './home-celular.component.html',
  styleUrls: ['./home-celular.component.scss']
})
export class HomeCelularComponent implements OnInit, OnDestroy {
  usuario: any = {};
  empresa: any = null;
  temModuloPedidos = false;
  temModuloMesas = false;
  exibirFinanceiro = false;
  podeOperarCaixa = false;
  podeGerenciarLoja = false;

  filaAgora: any = null;
  pedidosAbertos: any[] = [];
  agendados: any[] = [];
  hoje: any = null;
  anterior: any = null;
  horas: number[] = [];
  /** Período do card de vendas; "hoje" usa a carga de sempre, os outros carregam ao tocar. */
  periodo: PeriodoVendasCelular = 'hoje';
  readonly periodosVendas: Array<{id: PeriodoVendasCelular, nome: string}> = [
    {id: 'hoje', nome: 'Hoje'}, {id: 'ontem', nome: 'Ontem'}, {id: '7dias', nome: '7 dias'}, {id: '30dias', nome: '30 dias'}];
  private outrosPeriodos: {[periodo: string]: {resumo: any, anterior: any, horas: number[]}} = {};
  caixa: any;
  mesas: any;

  funcoes: FuncaoMenuV2[] = [];
  readonly uso = new UsoMenuV2(UsoMenuV2.armazenamentoLocal());

  folha: '' | 'qr' | 'mais' | 'entregador' | 'pagamento' = '';
  abaAtual: AbaComanda = 'todos';
  podeAlterarStatus = false;
  podeCancelarPedido = false;
  /** Id do pedido com ação em andamento: trava os botões dele. */
  processando: any = null;
  pedidoEmAcao: any = null;
  etapaEmAcao: EtapaComanda | null = null;
  statusEmAcao: string | null = null;
  escolhendoFormaDoSaldo = false;
  confirmandoRecusa = false;
  entregadores: any[] | null = null;
  /** A lista de pedidos não traz os itens; a home busca por pedido, como a tela de pedidos faz ao abrir um. */
  private itensPorPedido: {[id: string]: any[]} = {};
  private buscandoItens = new Set<string>();
  aviso = '';
  reabrindo = false;
  /** Quantas comandas a lista mostra antes do "Ver mais". */
  limiteLista = LISTA_INICIAL;
  /** O dono pediu a lista inteira: ela abre na própria home, com as abas por etapa. */
  listaCompleta = false;
  qrErro = false;

  private comandasEmCache: ComandaHomeCelular[] = [];
  private assinaturaComandas = '';
  private contagemNovos = -1;
  private timerAviso: any;
  private inscricoes: Subscription[] = [];

  constructor(private router: Router, private constants: ConstantsService, private monitorador: MonitoradorPedidos,
              private autorizacao: AutorizacaoService, private pedidosService: PedidosService,
              private vendasPedidoService: VendasPedidoService, private caixaService: CaixaService,
              private mesaService: MesaService, private empresasService: EmpresasService,
              private servidorImpressao: ServidorImpressaoExecutorService, private casca: FidelidadeComponent,
              private impressao: ImpressaoService, private nfce: NfceService, private contatosService: ContatosService,
              private entregadorService: EntregadorService) {}

  ngOnInit(): void {
    this.usuario = this.autorizacao.getUser() || {};

    // Garçom não tem Início: vai direto para o salão, como na home de sempre.
    if (this.usuario.garcom) {
      this.router.navigate(['/admin/pedidos-mesas']).then(() => {});
      return;
    }

    this.exibirFinanceiro = RotaGuardClient.visualizaRelatorios(this.usuario);
    this.podeOperarCaixa = RotaGuardClient.operarCaixa(this.usuario);
    this.podeGerenciarLoja = RotaGuardClient.gerenciaLoja(this.usuario);
    // Mesmas travas do mudar-status: mudar status e recusar têm permissão própria.
    this.podeAlterarStatus = RotaGuardClient.alterarStatusPedido(this.usuario);
    this.podeCancelarPedido = RotaGuardClient.cancelarPedido(this.usuario);

    this.inscricoes.push(this.constants.empresa$.subscribe((empresa: any) => {
      if (!empresa) return;
      this.empresa = empresa;
      this.monteCatalogo();
      this.carregueMesas();
    }));

    this.inscricoes.push(this.constants.moduloPedido$.subscribe((tem: any) => {
      const tinha = this.temModuloPedidos;
      this.temModuloPedidos = !!tem;
      if (this.temModuloPedidos && !tinha) this.carreguePedidos();
    }));

    this.inscricoes.push(this.constants.moduloMesasGarcom$.subscribe((tem: any) => {
      const tinha = this.temModuloMesas;
      this.temModuloMesas = !!tem;
      if (this.temModuloMesas && !tinha) this.carregueMesas();
    }));

    // O monitorador já consulta o servidor sozinho: só recarrego quando o placar dele muda.
    this.inscricoes.push(this.monitorador.resumoPedidos$.subscribe(() => {
      const novos = (this.monitorador.novosPedidos || []).length;
      if (novos === this.contagemNovos) return;
      this.contagemNovos = novos;
      this.carreguePedidos();
    }));

    // O modal de pausar é da casca; quando ele fecha a loja, o estado daqui acompanha.
    this.inscricoes.push(this.casca.lojaFechada.subscribe((resultado: any) => {
      if (resultado && resultado.fechou) this.recarregueEmpresa();
    }));

    this.carregueCaixa();
    this.carregueHoje();

    this.inscricoes.push(timer(60000, 60000).subscribe(() => {
      this.carreguePedidos();
      this.carregueCaixa();
      this.carregueMesas();
    }));
    this.inscricoes.push(timer(120000, 120000).subscribe(() => {
      this.carregueHoje();
      if (this.periodo !== 'hoje') this.carreguePeriodo(this.periodo, true);
    }));
  }

  ngOnDestroy(): void {
    this.inscricoes.forEach((inscricao: Subscription) => inscricao.unsubscribe());
    if (this.timerAviso) clearTimeout(this.timerAviso);
  }

  // ─────────────────────────── Carga ───────────────────────────

  carreguePedidos(): void {
    if (!this.temModuloPedidos) return;

    this.pedidosService.obtenhaResumoStatus().then((fila: any) => this.filaAgora = fila || null).catch(() => {});

    // O mesmo universo da aba "Em aberto" da tela de pedidos (sem comandas de mesa).
    this.pedidosService.listePedidos(0, 100, {emAberto: true}, EnumFiltroDePedidos.pedidos)
      .then((resposta: any) => {
        this.pedidosAbertos = (resposta && resposta.pedidos) || [];
        this.carregueItens();
      })
      .catch(() => {});

    this.pedidosService.listePedidos(0, 20, {emAberto: true}, EnumFiltroDePedidos.agendados)
      .then((resposta: any) => this.agendados = ((resposta && resposta.pedidos) || [])
        .filter((pedido: any) => this.ehHoje(pedido.horarioEntregaAgendada)))
      .catch(() => {});
  }

  /**
   * Hoje contra o mesmo dia da semana passada, até a mesma hora: restaurante
   * vende por dia da semana, e comparar a sexta com a quinta acusa queda à toa.
   */
  carregueHoje(): void {
    if (!this.exibirFinanceiro) return;

    const agora = new Date();
    const hoje = this.diaNoFiltro(agora);
    const semanaPassada = this.diaNoFiltro(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 7));
    const hora = agora.toTimeString().substr(0, 8);
    const filtro = {dl: '1', bl: '1', ms: '1'};

    this.vendasPedidoService.obtenhaResumo({...filtro, di: hoje, df: hoje})
      .then((resumo: any) => this.hoje = resumo || {qtde: 0, total: 0}).catch(() => {});
    this.vendasPedidoService.obtenhaResumo({...filtro, di: semanaPassada, df: `${semanaPassada} ${hora}`})
      .then((resumo: any) => this.anterior = resumo || null).catch(() => this.anterior = null);
    this.vendasPedidoService.obtenhaResumoHoras({...filtro, di: hoje, df: hoje})
      .then((resposta: any) => this.horas = ((resposta && resposta.horas) || []).map((item: any) => Number(item.total) || 0))
      .catch(() => this.horas = []);
  }

  /**
   * Ontem contra o mesmo dia da semana anterior; 7 e 30 dias contra os 7 e 30
   * dias de antes. Só carrega quando o dono toca no período.
   */
  private carreguePeriodo(periodo: PeriodoVendasCelular, forcar = false): void {
    if (!this.exibirFinanceiro || periodo === 'hoje' || (this.outrosPeriodos[periodo] && !forcar)) return;

    const dados = this.outrosPeriodos[periodo] || (this.outrosPeriodos[periodo] = {resumo: null, anterior: null, horas: []});
    const agora = new Date();
    const dia = (dias: number) => this.diaNoFiltro(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + dias));
    const intervalos: {[chave: string]: string[][]} = {
      ontem: [[dia(-1), dia(-1)], [dia(-8), dia(-8)]],
      '7dias': [[dia(-6), dia(0)], [dia(-13), dia(-7)]],
      '30dias': [[dia(-29), dia(0)], [dia(-59), dia(-30)]]
    };
    const [[di, df], [diAnterior, dfAnterior]] = intervalos[periodo];
    const filtro = {dl: '1', bl: '1', ms: '1'};

    this.vendasPedidoService.obtenhaResumo({...filtro, di, df})
      .then((resumo: any) => dados.resumo = resumo || {qtde: 0, total: 0}).catch(() => dados.resumo = dados.resumo || {qtde: 0, total: 0});
    this.vendasPedidoService.obtenhaResumo({...filtro, di: diAnterior, df: dfAnterior})
      .then((resumo: any) => dados.anterior = resumo || null).catch(() => dados.anterior = null);
    if (periodo === 'ontem') {
      this.vendasPedidoService.obtenhaResumoHoras({...filtro, di, df})
        .then((resposta: any) => dados.horas = ((resposta && resposta.horas) || []).map((item: any) => Number(item.total) || 0))
        .catch(() => dados.horas = []);
    }
  }

  carregueCaixa(): void {
    if (!this.podeOperarCaixa) return;

    this.caixaService.obtenhaCaixaAtual().then((resposta: any) => {
      const caixa = resposta && resposta.caixa;
      this.caixa = caixa ? {aberto: true, desde: this.hora(caixa.horarioAbertura),
        operador: caixa.operador && caixa.operador.nome ? String(caixa.operador.nome).split(' ')[0] : ''} : {aberto: false};
    }).catch(() => this.caixa = undefined);
  }

  carregueMesas(): void {
    if (!this.temModuloMesas || !this.empresa) return;

    this.mesaService.obtenhaMesas(this.empresa).then((mesas: any[]) => {
      const lista = (mesas || []).filter((mesa: any) => !mesa.removida);
      const ocupadas = lista.filter((mesa: any) => mesa.comandas && mesa.comandas.length);
      const valorDa = (comanda: any) => Number(comanda.valorTotal) || Number(comanda.valor) || 0;
      // `tempo` da mesa vem em horas inteiras; o minuto sai da comanda aberta há mais tempo.
      const minutosDa = (mesa: any) => {
        const aberturas = mesa.comandas.map((comanda: any) => new Date(String(comanda.horarioAbertura || '').replace(' ', 'T')).getTime())
          .filter((instante: number) => !isNaN(instante));
        return aberturas.length ? Math.max(0, Math.round((Date.now() - Math.min(...aberturas)) / 60000)) : (Number(mesa.tempo) || 0) * 60;
      };
      this.mesas = {total: lista.length, ocupadas: ocupadas.length,
        // A grade da operação: só as ocupadas, a mais antiga primeiro (o salão inteiro fica na tela de mesas).
        lista: ocupadas
          .map((mesa: any) => ({nome: mesa.nome, minutos: minutosDa(mesa),
            valor: mesa.comandas.reduce((soma: number, comanda: any) => soma + valorDa(comanda), 0)}))
          .sort((a: any, b: any) => b.minutos - a.minutos)
          .map((mesa: any) => ({...mesa, tempo: this.duracao(mesa.minutos)}))};
    }).catch(() => this.mesas = null);
  }

  private recarregueEmpresa(): void {
    this.empresasService.logada().then((empresa: any) => {
      if (!empresa || !this.empresa) return;
      this.empresa.fechadoTemporariamente = empresa.fechadoTemporariamente;
      this.empresa.estaAberta = empresa.estaAberta;
      this.empresa.estaRecebendoPedidos = empresa.estaRecebendoPedidos;
      this.empresa.mensagemPausaProgramada = empresa.mensagemPausaProgramada;
      this.empresa.horariosDoServico = empresa.horariosDoServico;
    }).catch(() => {});
  }

  /** Mesmo catálogo e mesma fase do menu: o que o menu guarda para depois, aqui só aparece na busca. */
  private monteCatalogo(): void {
    const catalogo = criarCatalogoParaUsuario(this.usuario, this.empresa, this.constants, this.servidorImpressao.ehUmServidorAtivo());
    this.funcoes = apliqueFaseDoOnboarding(catalogo, (this.empresa && this.empresa.onboarding) || null);
  }

  // ─────────────────────────── Momento do dia ───────────────────────────

  private get servico(): any {
    return ((this.empresa && this.empresa.horariosDoServico) || [])[0];
  }

  get estadoLoja(): 'aberta' | 'pausada' | 'fechada' {
    if (!this.empresa) return 'fechada';
    if (this.empresa.fechadoTemporariamente) return 'pausada';

    const servico = this.servico;
    const aberta = servico ? servico.estaAberta && servico.estaRecebendoPedidos
      : this.empresa.estaAberta && this.empresa.estaRecebendoPedidos;

    return aberta ? 'aberta' : 'fechada';
  }

  /** Só depois que a fila respondeu: antes disso a tela segue o estado da loja, sem pular. */
  get contaNova(): boolean {
    return this.temModuloPedidos && !!this.filaAgora && this.filaAgora.jaVendeu === false;
  }

  get momento(): MomentoHomeCelular {
    if (this.contaNova) return 'contaNova';
    if (this.estadoLoja !== 'fechada') return 'aberta';

    return this.proximaAbertura ? 'antes' : 'fechou';
  }

  /** Próximo turno de hoje que ainda não começou ("18:00"), ou null. */
  get proximaAbertura(): string | null {
    const agora = this.minutosDoDia(new Date());

    for (const turno of ((this.servico && this.servico.horariosHoje) || [])) {
      if (!turno.funciona || !turno.horarioAbertura) continue;
      if (this.minutosDaHora(turno.horarioAbertura) > agora) return String(turno.horarioAbertura).substr(0, 5);
    }

    return null;
  }

  get faltaParaAbrir(): string {
    const abertura = this.proximaAbertura;
    if (!abertura) return '';

    const minutos = this.minutosDaHora(abertura) - this.minutosDoDia(new Date());
    const horas = Math.floor(minutos / 60), resto = minutos % 60;

    return horas ? `daqui a ${horas}h${resto < 10 ? '0' : ''}${resto}` : `daqui a ${resto} min`;
  }

  /** Fração do intervalo até abrir já percorrida, para o anel do "Abre às". */
  get progressoAbertura(): number {
    const abertura = this.proximaAbertura;
    if (!abertura) return 0;

    const falta = this.minutosDaHora(abertura) - this.minutosDoDia(new Date());

    return Math.max(0.05, Math.min(1, 1 - falta / 240));
  }

  get fechamentoDeHoje(): string {
    const agora = this.minutosDoDia(new Date());

    for (const turno of ((this.servico && this.servico.horariosHoje) || [])) {
      if (!turno.funciona || !turno.horarioAbertura || !turno.horarioFechamento) continue;
      const abre = this.minutosDaHora(turno.horarioAbertura);
      let fecha = this.minutosDaHora(turno.horarioFechamento);
      if (fecha <= abre) fecha += 24 * 60;
      if (agora >= abre && agora < fecha) return String(turno.horarioFechamento).substr(0, 5);
    }

    return '';
  }

  get textoEstadoLoja(): string {
    if (this.estadoLoja === 'pausada') return this.empresa.mensagemPausaProgramada || 'Pausada. Reabra para voltar a receber pedidos.';
    if (this.estadoLoja === 'aberta') return this.fechamentoDeHoje ? `Recebendo pedidos até ${this.fechamentoDeHoje}` : 'Recebendo pedidos';

    const servico = this.servico;
    return (servico && (servico.mensagemAbrirPedidos || servico.motivo)) || 'Fora do horário de funcionamento';
  }

  // ─────────────────────────── Vendas de hoje ───────────────────────────

  get exibirHoje(): boolean {
    return this.exibirFinanceiro && this.temModuloPedidos && !!this.hoje;
  }

  get diaComparado(): string {
    const dia = new Date().getDay();
    const nome = DIAS_DA_SEMANA[dia];

    return dia === 0 || dia === 6 ? `${nome} passado` : `${nome} passada`;
  }

  escolhaPeriodo(periodo: PeriodoVendasCelular): void {
    this.periodo = periodo;
    this.carreguePeriodo(periodo);
  }

  /** O resumo do período escolhido; null enquanto carrega. */
  get resumoPeriodo(): any {
    return this.periodo === 'hoje' ? this.hoje : (this.outrosPeriodos[this.periodo] || {resumo: null}).resumo;
  }

  private get anteriorPeriodo(): any {
    return this.periodo === 'hoje' ? this.anterior : (this.outrosPeriodos[this.periodo] || {anterior: null}).anterior;
  }

  get totalPeriodo(): number {
    const resumo = this.resumoPeriodo;
    return Number(resumo && (resumo.totalComTaxa || resumo.total)) || 0;
  }

  get pedidosPeriodo(): number {
    return Number(this.resumoPeriodo && this.resumoPeriodo.qtde) || 0;
  }

  get ticketPeriodo(): number {
    return Number(this.resumoPeriodo && this.resumoPeriodo.ticketMedio) || 0;
  }

  get taxasPeriodo(): number {
    return Number(this.resumoPeriodo && this.resumoPeriodo.taxaDeEntrega) || 0;
  }

  get variacaoPeriodo(): {texto: string, sobe: boolean} | null {
    const antes = Number(this.anteriorPeriodo && (this.anteriorPeriodo.totalComTaxa || this.anteriorPeriodo.total)) || 0;
    if (!antes) return null;

    const pct = Math.round(((this.totalPeriodo - antes) / antes) * 100);
    if (!pct) return {texto: 'igual', sobe: true};

    return {texto: `${Math.abs(pct)}% a ${pct > 0 ? 'mais' : 'menos'}`, sobe: pct > 0};
  }

  /** Com quem o período foi comparado, escrito por extenso. */
  get comparacaoPeriodo(): string {
    if (this.periodo === 'ontem') return 'que o mesmo dia da semana anterior';
    if (this.periodo === '7dias') return 'que os 7 dias anteriores';
    if (this.periodo === '30dias') return 'que os 30 dias anteriores';
    return `que ${this.diaComparado} até ${new Date().toTimeString().substr(0, 5)}`;
  }

  /** De onde veio a venda do período, a mesma divisão da home do desktop. */
  get origens(): Array<{nome: string, total: number, qtde: number, fatia: number, cor: string}> {
    const linhas = ((this.resumoPeriodo && this.resumoPeriodo.vendasPorOrigemPedido) || [])
      .map((item: any) => {
        const chave = String(item.chave || '').trim();
        const nome = NOME_DA_ORIGEM[chave] || (chave ? chave.charAt(0).toUpperCase() + chave.slice(1).toLowerCase() : 'Outros');
        return {nome, total: Number(item.total) || 0, qtde: Number(item.qtde) || 0};
      })
      .filter((linha: any) => linha.total > 0 || linha.qtde > 0)
      .sort((a: any, b: any) => b.total - a.total)
      .slice(0, 6);
    const soma = linhas.reduce((acumulado: number, linha: any) => acumulado + linha.total, 0);

    return linhas.map((linha: any, i: number) => ({...linha, cor: CORES_DA_ORIGEM[i], fatia: soma ? Math.round((linha.total / soma) * 100) : 0}));
  }

  /** Barras por hora, do primeiro ao último horário com venda (no mínimo seis colunas); só em hoje e ontem. */
  get barrasHoras(): Array<{altura: number, melhor: boolean, rotulo: string}> {
    const horas = (this.periodo === 'hoje' ? this.horas : (this.outrosPeriodos[this.periodo] || {horas: []}).horas) || [];
    const comVenda = horas.map((valor: number, hora: number) => valor > 0 ? hora : -1).filter((hora: number) => hora >= 0);
    if (!comVenda.length) return [];

    let inicio = comVenda[0], fim = Math.max(comVenda[comVenda.length - 1], this.periodo === 'hoje' ? new Date().getHours() : 0);
    while (fim - inicio < 5) inicio > 0 ? inicio-- : fim++;
    fim = Math.min(23, fim);

    const fatia = horas.slice(inicio, fim + 1);
    const maior = Math.max(...fatia, 1);

    return fatia.map((valor: number, i: number) => ({
      altura: Math.max(6, Math.round((valor / maior) * 100)),
      melhor: valor === maior && valor > 0,
      rotulo: `${inicio + i}h`
    }));
  }

  /** Três marcas de hora embaixo das barras: a primeira, a do meio e a última. */
  get rotulosHoras(): string[] {
    const barras = this.barrasHoras;
    if (!barras.length) return [];
    return [barras[0].rotulo, barras[Math.floor(barras.length / 2)].rotulo, barras[barras.length - 1].rotulo];
  }

  get melhorHora(): string {
    const barra = this.barrasHoras.find((item: any) => item.melhor);
    return barra ? barra.rotulo : '';
  }

  abraRelatorioVendas(): void {
    this.router.navigateByUrl('/admin/financeiro-delivery-v2').then(() => {});
  }

  // ─────────────────────────── Precisa de você ───────────────────────────

  get toleranciaAtraso(): number {
    return (this.filaAgora && this.filaAgora.minutosTolerancia) || 60;
  }

  /**
   * Todo pedido em aberto, cada um com o próximo passo, mais as pendências que
   * travam a operação. Valor de um pedido é de todos (o operador cobra por ele);
   * o que a permissão de relatórios protege é o agregado do dia.
   */
  get comandas(): ComandaHomeCelular[] {
    const resumo = (this.monitorador.resumoPedidos && this.monitorador.resumoPedidos.value) || {};
    const assinatura = this.pedidosDeAgora.map((p: any) => `${p.id}:${p.statusOrdem}:${p.aceito}:${p.pago}:${p.duracao}`).join('|') +
      `#${resumo.totalErroParceiro}|${resumo.totalContestados}|${this.podeAlterarStatus}`;

    if (assinatura === this.assinaturaComandas) return this.comandasEmCache;

    const pedidos = ordeneParaOTrilho(this.pedidosDeAgora, this.toleranciaAtraso)
      .map(item => this.comandaDoPedido(item.pedido, item.etapa, item.atrasado));

    const pendencias: ComandaHomeCelular[] = [];
    const pendencia = (id: string, quantidade: number, singular: string, plural: string, nota: string, rota: string) => {
      if (!quantidade) return;
      pendencias.push({id, etapa: null, aba: 'todos', rotulo: 'Pendência', icone: 'fe-alert-circle', tom: 'pendencia',
        titulo: `${quantidade} ${quantidade === 1 ? singular : plural}`, meta: '', nota, valor: null, tempo: '',
        quente: false, itens: [], maisItens: 0, passo: null, rota});
    };
    // Pix aguardando já vem pedido a pedido; aqui só o que não aparece na lista.
    pendencia('erros', Number(resumo.totalErroParceiro) || 0, 'pedido com erro no parceiro', 'pedidos com erro no parceiro',
      'Confira antes que o cliente reclame.', '/admin/pedidos/erros');
    pendencia('contestados', Number(resumo.totalContestados) || 0, 'pedido contestado', 'pedidos contestados',
      'Responda a contestação no prazo.', '/admin/pedidos/contestados');

    this.assinaturaComandas = assinatura;
    this.comandasEmCache = [...pedidos.filter(c => c.quente), ...pendencias, ...pedidos.filter(c => !c.quente)];

    return this.comandasEmCache;
  }

  /** As abas com contagem; só aparecem as que têm pedido (e "Todos" quando há mais de uma). */
  get abas(): Array<{id: AbaComanda, nome: string, total: number}> {
    const conte = (aba: AbaComanda) => this.comandas.filter(c => c.aba === aba).length;
    const nomes: Array<[AbaComanda, string]> = [['aceitar', 'Aceitar'], ['pix', 'Pix aguardando'], ['preparo', 'Preparo'],
      ['prontos', 'Prontos'], ['rua', 'Na rua'], ['receber', 'A receber']];
    const abas = nomes.map(([id, nome]) => ({id, nome, total: conte(id)})).filter(aba => aba.total > 0);

    // Com uma etapa só, as abas não ajudam a andar: a lista mostra tudo.
    return abas.length > 1 ? [{id: 'todos', nome: 'Todos', total: this.comandas.length}, ...abas] : [];
  }

  get comandasVisiveis(): ComandaHomeCelular[] {
    if (this.abaAtual === 'todos' || !this.abas.some(aba => aba.id === this.abaAtual)) return this.comandas;
    return this.comandas.filter(c => c.aba === this.abaAtual);
  }

  get comandasNaLista(): ComandaHomeCelular[] {
    return this.comandasVisiveis.slice(0, this.limiteLista);
  }

  // ─────────────────────────── Saudação ───────────────────────────

  /** "Boa noite, Márcio" + a data, ou a hora do caixa para quem está no caixa. */
  get saudacao(): {titulo: string, detalhe: string} {
    const agora = new Date();
    const hora = agora.getHours();
    const periodo = hora >= 5 && hora < 12 ? 'Bom dia' : hora >= 12 && hora < 18 ? 'Boa tarde' : 'Boa noite';
    const nome = String((this.usuario && this.usuario.nome) || '').trim().split(' ')[0];
    const dia = DIAS_DA_SEMANA[agora.getDay()];
    const detalhe = this.visaoCaixa && this.caixa && this.caixa.aberto ? `Caixa aberto às ${this.caixa.desde}`
      : `${dia.charAt(0).toUpperCase() + dia.slice(1)}, ${agora.getDate()} de ${MESES[agora.getMonth()]}`;

    return {titulo: nome ? `${periodo}, ${nome}` : periodo, detalhe};
  }

  // ─────────────────────────── Urgências ───────────────────────────

  /** Quem vê vendas começa pelo negócio; os pedidos vêm encolhidos aos que pedem ação. */
  get visaoDono(): boolean {
    return this.exibirFinanceiro;
  }

  /** Opera o caixa mas não mexe em pedido: a home começa pela gaveta e pelo que falta receber. */
  get visaoCaixa(): boolean {
    return !this.visaoDono && this.podeOperarCaixa && !this.podeAlterarStatus;
  }

  get aReceber(): ComandaHomeCelular[] {
    return this.comandas.filter(c => c.aba === 'receber');
  }

  /** Venda no balcão é operar o caixa: só com a permissão e o recurso liberado no catálogo. */
  get funcaoBalcao(): FuncaoMenuV2 | undefined {
    return this.podeOperarCaixa ? this.funcoes.find((f: FuncaoMenuV2) => f.id === 'pedidos/pdv/novo') : undefined;
  }

  /** O que pede alguém agora: pedido para aceitar, atrasado, e as pendências de parceiro e contestação. */
  get urgentes(): ComandaHomeCelular[] {
    const ordem = (c: ComandaHomeCelular) => c.aba === 'aceitar' ? 0 : c.quente ? 1 : 2;
    return this.comandas.filter(c => c.aba === 'aceitar' || c.quente || c.tom === 'pendencia').sort((a, b) => ordem(a) - ordem(b));
  }

  /**
   * "3 pedidos precisam de você" + uma ficha por tipo. A cor diz o tipo: azul é a rotina (aceitar),
   * âmbar pede atenção (atrasado), vermelho é problema (erro no parceiro, contestação). Null sem nada.
   */
  get faixaUrgencias(): {total: number, titulo: string, partes: Array<{qtde: number, texto: string, cor: string}>} | null {
    const resumo = (this.monitorador.resumoPedidos && this.monitorador.resumoPedidos.value) || {};
    const pedidos = this.comandas.filter(c => c.pedido);
    const aceitar = pedidos.filter(c => c.aba === 'aceitar').length;
    const atrasados = pedidos.filter(c => c.quente && c.aba !== 'aceitar').length;
    const urgentes = pedidos.filter(c => c.aba === 'aceitar' || c.quente).length;
    const erros = Number(resumo.totalErroParceiro) || 0;
    const contestados = Number(resumo.totalContestados) || 0;
    const total = urgentes + erros + contestados;
    if (!total) return null;

    const partes = [
      {qtde: aceitar, texto: 'para aceitar', cor: 'azul'},
      {qtde: atrasados, texto: atrasados === 1 ? 'atrasado' : 'atrasados', cor: 'ambar'},
      {qtde: erros, texto: 'com erro no parceiro', cor: 'vermelho'},
      {qtde: contestados, texto: contestados === 1 ? 'contestado' : 'contestados', cor: 'vermelho'}
    ].filter(parte => parte.qtde > 0);

    return {total, titulo: total === 1 ? '1 pedido precisa de você' : `${total} pedidos precisam de você`, partes};
  }

  /** O dono vê até dois cartões: os urgentes ou, sem urgência, os próximos da fila. */
  get destaquesDono(): ComandaHomeCelular[] {
    return (this.urgentes.length ? this.urgentes : this.comandas).slice(0, 2);
  }

  /** Quantos pedidos em cada etapa, para o dono ler a fila sem a lista. */
  get etapasResumo(): Array<{nome: string, total: number}> {
    const nomes: Array<[AbaComanda, string]> = [['aceitar', 'aceitar'], ['pix', 'pix'], ['preparo', 'preparo'],
      ['prontos', 'prontos'], ['rua', 'na rua'], ['receber', 'a receber']];
    return nomes.map(([id, nome]) => ({nome, total: this.comandas.filter(c => c.aba === id).length})).filter(etapa => etapa.total > 0);
  }

  vaParaPedidos(): void {
    if (this.visaoDono) this.listaCompleta = true;
    setTimeout(() => {
      const secao = document.getElementById('hc-pedidos');
      if (secao) secao.scrollIntoView({behavior: 'smooth', block: 'start'});
    });
  }

  get comandasForaDaLista(): number {
    return Math.max(0, this.comandasVisiveis.length - this.limiteLista);
  }

  vejaMaisComandas(): void {
    this.limiteLista += LISTA_PASSO;
  }

  escolhaAba(aba: AbaComanda): void {
    this.abaAtual = aba;
    this.limiteLista = LISTA_INICIAL;
  }

  get pedidosDeAgora(): any[] {
    return this.pedidosAbertos.filter((pedido: any) => (Number(pedido.duracao) || 0) < MINUTOS_PEDIDO_ANTIGO);
  }

  /** Esquecidos em aberto (de outro turno ou dia): um aviso só, fora da lista e da contagem. */
  get pedidosAntigos(): {total: number, maisAntigo: string} {
    const antigos = this.pedidosAbertos.filter((pedido: any) => (Number(pedido.duracao) || 0) >= MINUTOS_PEDIDO_ANTIGO);
    const maior = Math.max(0, ...antigos.map((pedido: any) => Number(pedido.duracao) || 0));
    return {total: antigos.length, maisAntigo: this.duracao(maior)};
  }

  /** "agora", "25 min", "2h05", "3 dias". */
  duracao(minutos: number): string {
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `${minutos} min`;
    if (minutos < 24 * 60) return `${Math.floor(minutos / 60)}h${minutos % 60 < 10 ? '0' : ''}${minutos % 60}`;
    const dias = Math.floor(minutos / (24 * 60));
    return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  }

  private comandaDoPedido(pedido: any, etapa: EtapaComanda, atrasado: boolean): ComandaHomeCelular {
    const minutos = Number(pedido.duracao) || 0;
    const forma = pedido.formaDeEntrega || (pedido.mesa ? 'Mesa' : 'Balcão');
    const visual: {[etapa: string]: [string, string]} = {
      aceitar: ['Aceitar', 'fe-bell'], pix: ['Pix aguardando', 'fe-credit-card'], novo: ['Novo', 'fe-bell'],
      preparo: ['Em preparo', 'fe-clock'], pronto: ['Pronto', 'fe-package'], rua: ['Na rua', 'fe-truck'], receber: ['A receber', 'fe-dollar-sign']
    };
    const [rotulo, icone] = atrasado ? ['Atrasado', 'fe-alert-triangle'] : visual[etapa];
    const doPedido = (pedido.itens && pedido.itens.length) ? pedido.itens : (this.itensPorPedido[String(pedido.id)] || []);
    const itens = doPedido.filter((item: any) => item && item.descricao);

    return {
      id: String(pedido.id), etapa, aba: abaDaEtapa(etapa), rotulo, icone, pedido,
      tom: atrasado ? 'atraso' : etapa,
      titulo: this.clienteDoPedido(pedido),
      meta: `#${pedido.codigo}${forma ? ', ' + String(forma).toLowerCase() : ''}`,
      nota: this.notaDaComanda(pedido, etapa, atrasado),
      valor: Number(pedido.total) || 0,
      tempo: this.duracao(minutos),
      quente: atrasado,
      itens: itens.slice(0, 4).map((item: any) => ({qtde: this.qtde(item.qtde), descricao: item.descricao, observacao: item.observacao || ''})),
      maisItens: Math.max(0, itens.length - 4),
      passo: proximoPasso(pedido, etapa, this.podeAlterarStatus, this.podeAlterarStatus || this.podeOperarCaixa),
      rota: this.rotaDoPedido(pedido)
    };
  }

  private notaDaComanda(pedido: any, etapa: EtapaComanda, atrasado: boolean): string {
    if (etapa === 'pix') return 'Entra no preparo quando o pagamento cair.';
    if (atrasado) return `Passou dos ${this.toleranciaAtraso} min combinados.`;
    if (etapa === 'receber') return 'Entregue, mas o pagamento ainda não foi marcado.';
    if (pedido.pago) return 'Já pago.';
    return pedido.fiado ? 'Na conta do cliente (fiado).' : 'A receber na entrega.';
  }

  private rotaDoPedido(pedido: any): string {
    return pedido && pedido.guid ? `/admin/pedidos/detalhes/${pedido.guid}?t=${pedido.id}` : '/admin/pedidos';
  }

  private qtde(valor: any): string {
    const numero = Number(valor) || 0;
    return Number.isInteger(numero) ? String(numero) : numero.toLocaleString('pt-BR', {maximumFractionDigits: 3});
  }

  private clienteDoPedido(pedido: any): string {
    const nome = (pedido.cliente && pedido.cliente.nome) || (pedido.contato && pedido.contato.nome);
    if (nome) return nome;
    if (pedido.mesa) return `Mesa ${pedido.mesa.nome || pedido.mesa.numero || ''}`.trim();
    return 'Balcão';
  }

  /** Itens dos pedidos da lista que ainda não vieram, no máximo 20 por vez (os mais urgentes primeiro). */
  private carregueItens(): void {
    const faltando = ordeneParaOTrilho(this.pedidosDeAgora, this.toleranciaAtraso)
      .map(item => item.pedido)
      .filter((pedido: any) => pedido.guid && !(pedido.itens && pedido.itens.length) &&
        !this.itensPorPedido[String(pedido.id)] && !this.buscandoItens.has(String(pedido.id)))
      .slice(0, 20);

    for (const pedido of faltando) {
      const id = String(pedido.id);
      this.buscandoItens.add(id);
      this.pedidosService.obtenhaPorCodigo(pedido.guid).then((completo: any) => {
        this.itensPorPedido[id] = (completo && completo.itens) || [];
        this.assinaturaComandas = '';
      }).catch(() => {}).finally(() => this.buscandoItens.delete(id));
    }
  }

  /** Mesmo registro da tela de pedidos: agir no pedido é tê-lo visto, e o contador de novos da casca baixa. */
  private marqueVisualizado(pedido: any): void {
    if (!pedido || !pedido.id || pedido.visualizado) return;

    this.pedidosService.foiVisualizado(pedido).then(() => {
      pedido.visualizado = true;
      this.monitorador.atualizeFoiLido(pedido);
    }).catch(() => {});
  }

  abraComanda(comanda: ComandaHomeCelular): void {
    this.marqueVisualizado(comanda.pedido);
    this.router.navigateByUrl(comanda.rota, comanda.pedido ? {state: comanda.pedido} : undefined).then(() => {});
  }

  identifiqueComanda(indice: number, comanda: ComandaHomeCelular): string {
    return comanda.tom + comanda.id;
  }

  /** "Juliana Souza" → "JS"; o avatar do cartão de pedido. */
  iniciais(nome: string): string {
    const partes = String(nome || '').trim().split(/\s+/).filter(parte => /[a-zA-ZÀ-ú]/.test(parte));
    if (!partes.length) return '#';
    return (partes[0].charAt(0) + (partes.length > 1 ? partes[partes.length - 1].charAt(0) : partes[0].charAt(1) || '')).toUpperCase();
  }

  /** Cor fixa por pedido, para o mesmo cliente não mudar de cor entre uma carga e outra. */
  corDoAvatar(comanda: ComandaHomeCelular): string {
    const cores = ['azul', 'teal', 'rosa', 'roxo', 'ceu', 'ambar', 'verde'];
    const soma = String(comanda.id).split('').reduce((total: number, letra: string) => total + letra.charCodeAt(0), 0);
    return cores[soma % cores.length];
  }

  estaProcessando(comanda: ComandaHomeCelular): boolean {
    return !!comanda.pedido && this.processando === comanda.pedido.id;
  }

  // ─────────────────────── Ações na comanda ───────────────────────
  // Copiam o mudar-status e o detalhe do pedido: mesmo aviso ao cliente (ligado,
  // menos no balcão), mesma impressão automática, mesmo "Foi pago?" e pontos.

  /** Botão principal: o próximo passo da etapa. */
  executePasso(comanda: ComandaHomeCelular): void {
    const passo = comanda.passo;
    if (!passo || !comanda.pedido || this.processando) return;

    this.marqueVisualizado(comanda.pedido);

    if (passo.tipo === 'aceitar') {
      this.aceite(comanda.pedido);
    } else if (passo.tipo === 'pagar') {
      this.pergunteOPagamento(comanda.pedido, null);
    } else {
      this.vaParaStatus(comanda.pedido, passo.status);
    }
  }

  /** "Saiu" pergunta o entregador (se houver cadastro); "Entregue" pergunta se recebeu (como o "Foi pago?"). */
  vaParaStatus(pedido: any, status: string): void {
    this.folha = '';

    if (status === 'Saiu para entrega') {
      this.carregueEntregadores().then((entregadores: any[]) => {
        if (!entregadores.length) return this.mudeStatus(pedido, status, {});
        this.pedidoEmAcao = pedido;
        this.folha = 'entregador';
      });
      return;
    }

    if (status === 'Entregue' && perguntaPagamento(pedido)) {
      this.pergunteOPagamento(pedido, status);
      return;
    }

    this.mudeStatus(pedido, status, {});
  }

  escolhaEntregador(entregador: any): void {
    const pedido = this.pedidoEmAcao;
    this.folha = '';
    if (pedido) this.mudeStatus(pedido, 'Saiu para entrega', {entregador});
  }

  private pergunteOPagamento(pedido: any, status: string | null): void {
    this.pedidoEmAcao = pedido;
    this.statusEmAcao = status;
    this.escolhendoFormaDoSaldo = false;
    this.folha = 'pagamento';
  }

  /** Resposta da folha de pagamento. Parcial pede a forma do saldo, como a tela de hoje. */
  respondaPagamento(recebeu: boolean, forma: any = null): void {
    const pedido = this.pedidoEmAcao;
    if (!pedido) return;

    if (recebeu && pedido.pagamentoParcial && !forma) {
      this.escolhendoFormaDoSaldo = true;
      return;
    }

    this.folha = '';

    if (this.statusEmAcao) {
      this.mudeStatus(pedido, this.statusEmAcao, recebeu ? {pago: true, forma} : {});
    } else if (recebeu) {
      this.marquePago(pedido, forma);
    }
  }

  /** Formas para o saldo de pagamento parcial: as mesmas que o mudar-status oferece. */
  get formasDoSaldo(): any[] {
    return ((this.empresa && this.empresa.formasDePagamento) || []).filter((forma: any) => !forma.online && !forma.fiado);
  }

  get textoPagamento(): string {
    const pedido = this.pedidoEmAcao;
    if (!pedido) return '';
    const valor = pedido.pagamentoParcial && pedido.saldoPendente ? pedido.saldoPendente : pedido.total;
    const forma = this.formaDoPedido(pedido);
    return `Recebeu ${this.moeda(valor)}${forma ? ' em ' + forma.toLowerCase() : ''}?`;
  }

  private formaDoPedido(pedido: any): string {
    const pagamento = (pedido.pagamentos || [])[0];
    const forma = pagamento && (pagamento.formaDePagamento || pagamento.forma);
    return (forma && (forma.descricao || forma.nome)) || (typeof pagamento?.descricao === 'string' ? pagamento.descricao : '');
  }

  private async aceite(pedido: any): Promise<void> {
    this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.aceitePedido(pedido);
      pedido.aceito = true;
      this.mostreAviso(resposta && resposta.erroNotificar ? `Aceito, mas: ${resposta.erroNotificar}` : 'Pedido aceito.');
      await this.imprima(pedido);
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível aceitar. Tente de novo.'));
    } finally {
      this.processando = null;
      this.assinaturaComandas = '';
      this.carreguePedidos();
    }
  }

  private async mudeStatus(pedido: any, status: string, extras: {entregador?: any, pago?: boolean, forma?: any}): Promise<void> {
    const codigo = CODIGO_DO_STATUS[status];
    if (codigo === undefined) return;

    const eraNovo = Number(pedido.statusOrdem) === EnumStatusPedido.Novo;
    this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.altereStatus(pedido, codigo, !pedido.balcao);
      pedido.statusOrdem = codigo;
      pedido.status = status;
      let avisoExtra = resposta && resposta.erroNotificar ? ` Aviso ao cliente: ${resposta.erroNotificar}` : '';

      if (codigo === EnumStatusPedido.EmPreparacao && this.empresa && this.empresa.aceitarPedidoAutomatico &&
          this.empresa.configImpressao && this.empresa.configImpressao.momentoImprimirAuto !== EnumMomentoImprimirAuto.NovoPedido) {
        await this.imprima(pedido);
      }

      if (extras.entregador) {
        await this.pedidosService.altereEntregador(pedido, extras.entregador);
        pedido.objetoEntregador = extras.entregador;
      }

      if (eraNovo) this.monitorador.alterouStatusPedido(pedido);

      if (extras.pago) {
        const pontos = await this.marquePago(pedido, extras.forma, true);
        if (pontos) return;
      }

      this.mostreAviso(`#${pedido.codigo}: ${status.toLowerCase()}.${avisoExtra}`);
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível mudar o status. Tente de novo.'));
    } finally {
      this.processando = null;
      this.assinaturaComandas = '';
      this.carreguePedidos();
    }
  }

  /** Marca como pago; se a loja pontua no pagamento, segue para lançar os pontos (como a tela de hoje). */
  private async marquePago(pedido: any, forma: any, dentroDeOutraAcao = false): Promise<boolean> {
    if (!dentroDeOutraAcao) this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.altereFoiPago(pedido, true, forma);
      pedido.pago = true;

      if (resposta && resposta.redirectAdicionarPontos) {
        await this.adicionePontos(pedido.cliente || pedido.contato, pedido.subvalor);
        return true;
      }

      if (!dentroDeOutraAcao) this.mostreAviso('Pagamento registrado.');
      else this.mostreAviso(`#${pedido.codigo}: entregue e pago.`);
      return false;
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível registrar o pagamento.'));
      return false;
    } finally {
      if (!dentroDeOutraAcao) {
        this.processando = null;
        this.assinaturaComandas = '';
        this.carreguePedidos();
      }
    }
  }

  /** Mesmo caminho do mudar-status: cliente com cartão vai lançar pontos; sem cartão, cadastrar. */
  private async adicionePontos(contato: any, valor: number): Promise<void> {
    let completo = contato;
    const telefone = this.telefoneCompleto(contato);

    if (telefone) {
      try { completo = (await this.contatosService.obtenhaContato(telefone)) || contato; } catch (e) {}
    }

    const state: any = {contato: completo, valor};
    const urlRetorno = this.router.url;

    if (completo && completo.cartoes && completo.cartoes.length) {
      if (completo.cartoes.length === 1) state.cartao = completo.cartoes[0];
      await this.router.navigateByUrl('/admin/contatos/pontos/adicionar?urlRetorno=' + urlRetorno, {state});
    } else {
      await this.router.navigateByUrl('/admin/contatos/novo?urlRetorno=' + urlRetorno, {state});
    }
  }

  private telefoneCompleto(contato: any): string | null {
    if (!contato) return null;
    if (contato.codigoPais && contato.telefone) return String(contato.codigoPais) + String(contato.telefone);
    if (typeof contato.telefone === 'string' && contato.telefone.indexOf('+') === 0) return contato.telefone;
    return null;
  }

  /** Impressão automática com o pedido completo (as categorias decidem as impressoras). */
  private async imprima(pedido: any): Promise<void> {
    if (!pedido || !pedido.guid || !this.empresa) return;

    try {
      const completo: any = await this.pedidosService.obtenhaPorGuid(pedido.guid);
      if (!completo || !completo.id) return;

      const tela = new TelaImprimirPedido(this.impressao, this.pedidosService, this.nfce);
      tela.empresa = this.empresa;
      tela._pedido = completo;
      await tela.envieImpressaoAutomatica();
    } catch (erro) {
      // Sem impressora (celular sem QZ Tray): o pedido segue, como no detalhe.
    }
  }

  // ── "Mais" ──

  abraMais(comanda: ComandaHomeCelular): void {
    if (!comanda.pedido) return this.abraComanda(comanda);
    this.marqueVisualizado(comanda.pedido);
    this.pedidoEmAcao = comanda.pedido;
    this.etapaEmAcao = comanda.etapa;
    this.confirmandoRecusa = false;
    this.folha = 'mais';
  }

  /** Status para onde o pedido pode ir, menos o atual (cancelar continua no detalhe, que pede motivo). */
  get statusDaFolhaMais(): string[] {
    const pedido = this.pedidoEmAcao;
    if (!pedido || this.etapaEmAcao === 'aceitar') return [];
    return statusPermitidos(pedido, this.podeAlterarStatus).filter(status => CODIGO_DO_STATUS[status] !== Number(pedido.statusOrdem));
  }

  get podeRecusarAqui(): boolean {
    return this.etapaEmAcao === 'aceitar' && this.podeCancelarPedido;
  }

  /** Recusa direta só para pedido próprio; integração pede o motivo da plataforma, que está no detalhe. */
  async recuse(): Promise<void> {
    const pedido = this.pedidoEmAcao;
    if (!pedido) return;

    if (pedido.origemExterna) {
      this.folha = '';
      this.abraRota(this.rotaDoPedido(pedido));
      return;
    }

    if (!this.confirmandoRecusa) {
      this.confirmandoRecusa = true;
      return;
    }

    this.folha = '';
    this.processando = pedido.id;

    try {
      const resposta: any = await this.pedidosService.rejeitePedido(pedido);
      if (resposta && resposta.aguardandoPdv) {
        this.abraRota(this.rotaDoPedido(pedido));
        return;
      }
      this.mostreAviso(`Pedido #${pedido.codigo} recusado.`);
    } catch (erro) {
      this.mostreAviso(this.mensagemDeErro(erro, 'Não foi possível recusar. Tente de novo.'));
    } finally {
      this.processando = null;
      this.assinaturaComandas = '';
      this.carreguePedidos();
    }
  }

  abraPedidoEmAcao(): void {
    const pedido = this.pedidoEmAcao;
    this.folha = '';
    if (pedido) this.router.navigateByUrl(this.rotaDoPedido(pedido), {state: pedido}).then(() => {});
  }

  private carregueEntregadores(): Promise<any[]> {
    if (this.entregadores) return Promise.resolve(this.entregadores);

    return this.entregadorService.liste().then((lista: any) => {
      this.entregadores = (lista || []).filter((entregador: any) => entregador && entregador.ativo !== false);
      return this.entregadores;
    }).catch(() => []);
  }

  private mensagemDeErro(erro: any, padrao: string): string {
    if (typeof erro === 'string' && erro) return erro;
    return (erro && (erro.message || erro.mensagem)) || padrao;
  }

  // ─────────────────────────── Agora na loja ───────────────────────────

  get exibirCaixa(): boolean {
    return this.podeOperarCaixa && this.caixa !== undefined;
  }

  /** Pedido em aberto, mesa ocupada ou caixa aberto depois que a loja fechou. */
  get pendenciasFechamento(): boolean {
    return (this.temModuloPedidos && this.pedidosAbertos.length > 0) || (this.exibirMesas && this.mesas.ocupadas > 0)
      || (this.exibirCaixa && this.caixa.aberto);
  }

  /** Caixa e mesas lado a lado só quando os dois aparecem; sozinho, cada um ocupa a linha. */
  get bentoDuplo(): boolean {
    return this.exibirCaixa && this.caixa.aberto && this.exibirMesas && this.visaoDono;
  }

  /** Anel de ocupação das mesas: quanto do contorno fica pintado (circunferência de raio 15). */
  get ocupacaoMesas(): {circunferencia: number, resto: number} {
    const circunferencia = 2 * Math.PI * 15;
    const fracao = this.mesas && this.mesas.total ? this.mesas.ocupadas / this.mesas.total : 0;
    return {circunferencia, resto: circunferencia * (1 - fracao)};
  }

  get mesasNaGrade(): Array<{nome: string, valor: number, tempo: string}> {
    return ((this.mesas && this.mesas.lista) || []).slice(0, 6);
  }

  get exibirMesas(): boolean {
    return this.temModuloMesas && !!this.mesas;
  }

  pausarLoja(): void {
    this.casca.abraModalFecharLoja();
  }

  reabrirLoja(): void {
    if (this.reabrindo) return;
    this.reabrindo = true;

    this.empresasService.abra().then(() => {
      this.empresa.fechadoTemporariamente = false;
      delete this.empresa.mensagemPausaProgramada;
      this.mostreAviso('Loja reaberta.');
      this.recarregueEmpresa();
    }).catch(() => this.mostreAviso('Não foi possível reabrir. Tente de novo.'))
      .finally(() => this.reabrindo = false);
  }

  abraRota(rota: string): void {
    this.router.navigateByUrl(rota).then(() => {});
  }

  // ─────────────────────────── Vender e funções ───────────────────────────

  get portasDeVenda(): Array<{funcao: FuncaoMenuV2, nome: string, detalhe: string, icone: string, cor: string}> {
    return PORTAS_DE_VENDA
      .map(porta => ({...porta, funcao: this.funcoes.find((f: FuncaoMenuV2) => f.id === porta.id)}))
      // O catálogo libera o PDV de balcão pelo recurso da loja; vender no balcão é operar o caixa.
      .filter(porta => !!porta.funcao && (porta.id !== 'pedidos/pdv/novo' || this.podeOperarCaixa))
      .map(porta => porta.id === 'pedidos-mesas' && this.exibirMesas
        ? {...porta, detalhe: `${this.mesas.ocupadas} de ${this.mesas.total} ocupadas`} : porta);
  }

  /** Os mais usados neste aparelho; sem histórico, as telas principais do catálogo. */
  get atalhos(): FuncaoMenuV2[] {
    const vender = PORTAS_DE_VENDA.map(porta => porta.id);

    return this.funcoes
      .filter((f: FuncaoMenuV2) => !f.interna && !f.externa && f.grupo !== 'inicio' && f.grupo !== 'conta' && !vender.includes(f.id)
        && (f.acao ? false : !!f.rota) && (!f.somenteBusca || this.uso.pontuacao(f.id) > 0))
      .map((f: FuncaoMenuV2, ordem: number) => ({f, ordem, importancia: f.peso + this.uso.pontuacao(f.id) * 2}))
      .sort((a, b) => b.importancia - a.importancia || a.ordem - b.ordem)
      .slice(0, 8)
      .map(item => item.f);
  }

  corDaFuncao(funcao: FuncaoMenuV2): string {
    return COR_DO_GRUPO[funcao.grupo] || 'cinza';
  }

  /** Ícone do catálogo: `fe-*` direto; o da Mia vem em Font Awesome. */
  iconeDaFuncao(icone: string): string {
    return icone || 'fe-circle';
  }

  /** Mesmo caminho do menu-v2: registra o uso (alimenta os atalhos) e respeita ação, rota ou link externo. */
  execute(funcao: FuncaoMenuV2): void {
    this.monteCatalogo();
    const atual = this.funcoes.find((f: FuncaoMenuV2) => f.id === funcao.id);
    if (!atual) return;

    this.uso.registrar(atual.id);
    this.folha = '';

    if (atual.acao) {
      if (atual.acao === 'abrirLoja') this.casca.abraModalReabrirLoja();
      else if (atual.acao === 'fecharLoja') this.casca.abraModalFecharLoja();
      else if (atual.acao === 'impressao') this.casca.abraModalGerenciarJobs();
      else if (atual.acao === 'sair') this.casca.logout();
      else this.router.navigateByUrl('/admin/pedidos').then(() => {});
    } else if (atual.rota && !atual.externa) {
      this.router.navigateByUrl(atual.rota).then(() => {});
    } else if (atual.rota) {
      window.open(atual.rota, '_blank', 'noopener,noreferrer');
    }
  }

  fecheFolha(): void {
    this.folha = '';
    this.pedidoEmAcao = null;
    this.etapaEmAcao = null;
    this.statusEmAcao = null;
    this.confirmandoRecusa = false;
    this.escolhendoFormaDoSaldo = false;
  }

  @HostListener('document:keydown.escape')
  aoApertarEsc(): void {
    if (this.folha) this.fecheFolha();
  }

  // ─────────────────────────── Divulgar ───────────────────────────

  get linkCardapio(): string {
    return (this.empresa && this.empresa.linkLoja) || '';
  }

  get linkCurto(): string {
    return this.linkCardapio.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  compartilhe(): void {
    if (!this.linkCardapio) return;

    const texto = `Peça pelo nosso cardápio: ${this.linkCardapio}`;
    const nav: any = navigator;

    if (nav.share) {
      nav.share({title: this.empresa.nome, text: 'Peça pelo nosso cardápio:', url: this.linkCardapio}).catch(() => {});
      return;
    }

    window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank', 'noopener,noreferrer');
  }

  copieLink(mensagem = 'Link copiado.'): void {
    if (!this.linkCardapio) return;

    const area: any = navigator && (navigator as any).clipboard;
    const copie = area && area.writeText ? area.writeText(this.linkCardapio) : Promise.reject();

    copie.then(() => this.mostreAviso(mensagem)).catch(() => this.mostreAviso(this.linkCurto));
  }

  abraLoja(): void {
    if (this.linkCardapio) window.open(this.linkCardapio, '_blank', 'noopener,noreferrer');
  }

  // ─────────────────────────── Utilitários ───────────────────────────

  mostreAviso(texto: string): void {
    this.aviso = texto;
    if (this.timerAviso) clearTimeout(this.timerAviso);
    this.timerAviso = setTimeout(() => this.aviso = '', 2400);
  }

  moeda(valor: any): string {
    return (Number(valor) || 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});
  }

  /** "684,30" sem o símbolo: o "R$" vai menor ao lado. */
  valorSemSimbolo(valor: any): string {
    return (Number(valor) || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  hora(horario: any): string {
    if (!horario) return '';
    const data = new Date(horario);
    return isNaN(data.getTime()) ? '' : data.toTimeString().substr(0, 5);
  }

  clienteAgendado(pedido: any): string {
    return this.clienteDoPedido(pedido);
  }

  private ehHoje(horario: any): boolean {
    if (!horario) return false;
    const data = new Date(horario);
    return !isNaN(data.getTime()) && data.toDateString() === new Date().toDateString();
  }

  private diaNoFiltro(data: Date): string {
    const dois = (n: number) => (n < 10 ? '0' : '') + n;
    return `${data.getFullYear()}${dois(data.getMonth() + 1)}${dois(data.getDate())}`;
  }

  private minutosDoDia(data: Date): number {
    return data.getHours() * 60 + data.getMinutes();
  }

  private minutosDaHora(hora: string): number {
    return Number(String(hora).substr(0, 2)) * 60 + Number(String(hora).substr(3, 2));
  }
}
